import React from "react";
import GuideLayout from "../../components/layout/GuideLayout";
import Section from "../../components/layout/Section";
import Subheader from "../../components/layout/Subheader";
import {
  Typography,
  Link,
  Box
} from "@mui/material";
import BulletList from "../../components/layout/BulletList";
import { useTheme } from "@mui/material/styles";
import AccordionFAQ from "../../components/layout/AccordionFAQ";

const valueFactors = [
  "Item type",
  "Item rarity / availability",
  "Cost",
  "DTI / JN ratio",
  "Perceived popularity",
];

const valuesRuleOfThumb = [
  "Regular item in grams = 1 cap",
  "Regular item in events = value/cost of 1 event ticket",
  "Dyeworks item (current rotation) = 1 cap",
  "Regular item in a typical mystery capsule starts at 1 cap",
  "LE/Bonus items in typical mystery capsules start at 3 caps",
  "Pet day cap LEs might start higher than regular cap LEs",
];


const faqItems = [
  {
    question: "Values are so complicated. Aren't there people who don't care for values?",
    answer: (<>
      <Typography paragraph>
        Yes! It might not be as common, but there are people who don't care for values and just want to trade what they have for what they want. No one has to abide by the value system or agree with the values from the community, however, if you plan to trade frequently, you might have difficulties finding trades.
      </Typography>

      <Typography paragraph>
        In terms of NP trading, you can try thinking of it like this: No one can stop you from offering a codestone on a royal paint brush trading post lot, but that doesn't mean that your offer will be accepted. On the other hand, if you're offering a royal paint brush for a codestone, you'll likely find someone who would accept that trade quickly.
      </Typography>
    </>),
  },
  {
    question: "Why is everything so reliant on boxes? Don't we get free ones from Trudy?",
    answer: (<>
      <Typography paragraph>
        For regular traders, they can go through a lot of boxes quickly. It's correct that we get free ones from Trudy's Surprise, but it's definitely not enough to sustain traders. You can also get 2 boxes per NC card redeemed, but again, it's not necessarily enough for many regular traders + not everyone buys NC cards from third party sellers. (Fewer people buy NC cards nowadays too after the price increases.) Boxes are always in demand, so GBCs are pretty stable.
      </Typography>
    </>),
  },
  {
    question: "I tried asking for a value check / help but no one replied to my post! How long does it take for a response?",
    answer: (<>
      <Typography paragraph>
        If you're on the boards, try bumping your board every once in a while. If you're asking on discord or a different community, try asking again after a while. Response times vary as there are times when posts are missed or dead hours where not that many people are online.
      </Typography>
    </>),
  },
];

const sections = [
  { id: "top", title: "Top" },
  { id: "summary", title: "The Short(ish) Version" },
  { id: "the-cap-value", title: "The Cap Value" },
  { id: "applying-cap-value", title: "Applying the Cap Value Standard" },
  {
    id: "value",
    title: "What Goes into a Value?",
    children: [
      { id: "direct", title: "Direct Buyable Items" },
      { id: "buyable", title: "Buyable, But Not Direct" },
      { id: "retired", title: "Retired Items" }
    ]
  },
  {
    id: "determining-values",
    title: "How to Determine Values",
    children: [
      { id: "trade-data", title: "Trade Data" },
      { id: "ratios", title: "Ratios" },
      { id: "retire-date", title: "Retirement Date / Similar Items" }
    ]
  },
  { id: "our-recommendations", title: "Our Recommendations" },
  { id: "lingering-questions", title: "Lingering Questions" },
  { id: "final-words", title: "Final Words" },

];

const ValuesPage = () => {
  const theme = useTheme();

  return (
    <GuideLayout sections={sections}>
      <Section id="top" title="Ways to Determine and Interpret Values">
        <Typography
          variant="caption"
          color="secondary"
          sx={{ mt: -1, mb: 2, display: "block" }}
        >
          Last updated: June 2026
        </Typography>
        <Typography paragraph>
          NC trading revolves around NC values, but what goes into a value? If you're new to NC trading, figuring out values probably sounds like a nightmare. You might hear terms like "personal values" or "ratios" and have no idea what that means. Well, if it makes you feel any better, sometimes even regular traders have no idea either! (hahaha . . . pain . . .)
        </Typography>
        <Typography paragraph>
          In this article, we'll go over values in all its entirety - where values come from, common ways to determine values, interpreting values, and recommended practices. <b>Bear with us because this will be super lengthy - there's a lot that goes into NC trading!</b>
        </Typography>
        <Typography paragraph>
          Let's make one thing clear before we dive in: NC trading is subjective. There is no one "right" way to determine values because different people will have different interpretations and opinions. For that reason, we primarily focus on the more common methods being used and we hope this article will provide enough information to use as a decent <b>starting point</b> to navigate values.
        </Typography>
        <Typography paragraph>
          If you're looking for specific token valuations, this might not be the page for you. The general concepts of this article will apply to valuing tokens, but we do not go in depth for tokens here. Instead, check out our <Link href="/guides/token-trading">token trading guide</Link>!
        </Typography>
      </Section>

      <Section id="summary" title="The Short(ish) Version">

        <Typography paragraph>
          We know this article is super long, so here's a short(ish) version! This focuses on the most common schools of thought and will not cover everything. If you want further explanations as to why or how, that's what the rest of the article is for!

        </Typography>
        <Typography paragraph>
          Values are subjective, but the commonly accepted standard is 1 cap = ~200NC. Values can also take on a range like 2-3 caps for example, to which a fair equivalent would be anything from 400NC-600NC. Personal values are values that you assign based on your own criteria for what an item is worth. Some develop personal values through the offering / seeking ratio on DTI to measure supply and demand, and others use past trade data. Many people do a balance of both, where they look for past trade history as a starting point, and adjust based on ratios.

        </Typography>
        <Typography paragraph>
          The type of item can also affect its value. LE items usually start higher at 3 because they are not guaranteed items. How easy an item is to obtain, the <Link href="https://classic.support.neopets.com/hc/en-us/articles/37898249845261-NC-Mall-Drop-Rates">drop rates (which are published now!)</Link>, re-release schedules, and the popularity of an item can all affect an item's value. We recommend looking at all information available to form your own opinion!

        </Typography>
        <Typography paragraph>
          <b>Most importantly, you don't have to agree on values! If you don't feel good about a trade offer, it's okay to pass on it and move on!</b>

        </Typography>
      </Section>

      <Section id="the-cap-value" title="The Cap Value">
        <Typography paragraph>
          The standard for NC trading is 1 cap = 200NC, which comes from the regular price of a gift box mystery capsule (GBC). NC items are valued in caps, which is short for capsules, typically GBCs. When we say GBC or cap in this article, it refers to an item that has “gift box mystery capsule” in the name and guarantees at least 2 Basic Gift Boxes when opened (you can check item description for that). There's a new GBC design released every month, but generally, if someone is looking for GBCs and does not specify further, any GBC will suffice regardless of design.

        </Typography>
        <Typography paragraph>
          <strong>Why is this important?</strong> GBCs are the de facto currency in NC trading. In order to trade an item, a gift box is required. Gift boxes are not tradeable themselves, but you can obtain gift boxes through GBCs, which are tradeable. NC trading is reliant on using gift boxes, so GBCs are the community adopted standard to measure values.

        </Typography>
      </Section>

      <Section id="applying-cap-value" title="Applying the Cap Value Standard">
        <Typography paragraph>
          This section is gonna sound so insane if you're new to trading, but please remember that NC trading is subjective and there's a lot of nuances that we are attempting to explain and clear up.
        </Typography>

        <Typography paragraph>
          In the last section, we established the standard cap value (200NC), and now it's time to get a bit flexible with that. If you are trading custom (buying items in the mall to trade for other nc items), then figuring out the value to NC amount is pretty straightforward - just go by 200NC increments. If someone values their item at 2 caps, then items that cost a total of 400NC would be a fair offer as 2 x 200NC = 400NC.
        </Typography>

        <Typography paragraph>
          Now what about items that aren't exactly 200NC in the mall? What about an item that costs 150NC? Or 250NC? Many traders still consider it to be 1 cap since even sending a 150NC item still requires one box to send. Our recommendation is try not to get too technical with nickel and diming NC amounts when it comes to a small difference - extend your fellow traders the courtesy of an easy trade and others will for you too in the future. Of course, if you're not happy with a trade offer, always feel to decline and move on!
        </Typography>

        <Typography paragraph>
          In other cases, items can also be valued with a range, like 1-2 caps. Some traders like to take the average, so something at 1-2 means 1.5 caps (equivalent to 300NC), though it's also common to use the ranges as is and be flexible, so 1-2 caps can mean that anything between 200NC-400NC is a fair/equivalent value.
        </Typography>

        <Typography paragraph>
          We highlight the importance of flexibility with values because values and trades are not black and white. A 1 cap item trading for 1 GBC is easy, but when you're trading multiple items and venture into pricier items with larger ranges, it can quickly get confusing. For example, you might see something valued at 20-25. If you offer 4 items valued at 5-7 each, would that be a fair trade? Some would say yes, others might say no, though many would say it depends on the actual items because they have their own personal value for them.
        </Typography>

        <Typography paragraph>
          <b>Personal values</b> are values that you assign based on your own criteria for what an item is worth. You might value an item at 3 while someone else has a personal value of 2, and both would be fine. This entire article serves to provide insight on common ways you might use to develop your personal values as well as understand where others may draw their values from.
        </Typography>
      </Section>

      <Section id="value" title="What Goes Into a Value?">
        <Typography paragraph>
          Let's dig further and talk about what actually goes into a value. Have you ever thought to yourself “wow - why are people trading that for so much? it's not even a good item!” when you see an item trading for a high cap value? Personal values are what you assign, but let's also talk about how to interpret values that others put out in the world.
        </Typography>
        Typically, an item's value will depend one or more of these factors:
        <BulletList items={valueFactors} />

        <Subheader title="Direct Buyable Items" id="direct" />
        <Typography paragraph>
          These are items directly buyable in the mall, meaning they are guaranteed by purchasing and have no luck factor involved (unlike items in mystery capsules that we'll cover later). This will be pretty straightforward - just divide the price by 200NC to get a cap value. For example, the Deluxe Styling Studio Supplies costs 1400NC, so 1400/200 = 7 cap value. When an item is in the mall, it has a stable value because anyone can obtain it just by buying.
        </Typography>

        <Subheader title="Buyable, But Not Direct" id="buyable" />
        <Typography paragraph>
          These would be the items inside mystery capsules or through dyeworks for example. They are technically available in the mall, but you get them through purchasing a different item. Values can fluctuate with these because in most cases, there is a luck factor involved and the item is not guaranteed.
        </Typography>

        The value of these can vary based on the other factors listed previously, but as a rule of thumb:
        <BulletList items={valuesRuleOfThumb} />
        <Typography paragraph>
          Regular items from grams and events are pretty stable as you're able to obtain them fairly easily just by buying the gram/event ticket. Likewise, all variations of current dyeworks are usually 1 cap or 1 dyepot as they are easily obtainable.
        </Typography>

        <Typography paragraph>
          A typical mystery capsule has a relatively small pool of items, and each regular non-LE item starts at 1 cap - the cost of the mystery capsule itself. LE/Bonus items from these caps require luck - they are not guaranteed and have a lower drop rate. In many cases, LE items start 3 and again, they may go up or down depending on the other factors like perceived popularity from the community.
        </Typography>

        <Typography paragraph>
          Pet day caps are a little special, because the regular pool of items involve species specific wearables. From what we observed, most regular items start around 1 cap, but the LE items from these caps can often start higher than regular cap LEs. We avoid giving a specific number because it can fluctuate greatly from cap to cap. In comparison to regular capsules, people seem to buy fewer pet caps. Part of the reason may be the species restriction and the cost since pet caps do not have a bundle option. These can all contribute to less availability
        </Typography>

        <Typography paragraph>
          Please use these values as a starting point and <b>NOT</b> law because there are <b>always exceptions</b>! There are cases where an item is widely perceived to be better than its LE counterpart and therefore, some may value it higher, so be sure to browse and ask around if you're unsure!
        </Typography>


        <Subheader title="Retired Items" id="retired" />
        <Typography paragraph>
          These items are no longer obtainable in the mall and to get a retired item, you have to trade with a user who has it. Retired items have a huge range of values but again will largely depend on the factors mentioned in the beginning of the section. Many people will value retired items based on past trade history and ratios, which we'll cover in the next section.
        </Typography>

        <Typography paragraph>
          <b>Special Note:</b> Retired items can be rereleased through rerelease caps in the mall. However, just because it is technically rereleased, it does not mean the value will plummet everytime as buying a rerelease cap does not necessarily guarantee you the item. The value may change slightly and sometimes not at all due to factors like the overall size of the prize pool, demand for the item, and how many people buy the cap and pull the item.
        </Typography>
      </Section>



      <Section id="determining-values" title="How to Determine Values">
        <Typography paragraph>
          This is the big question right? How do you begin to value an item? We'll take a look at some common ways traders value items nowadays.
        </Typography>

        <Subheader title="Trade Data" id="trade-data" />

        <Typography paragraph>
          Many people choose to value their items based on past trade reports. You can check past trade reports here by searching for an item in our search bar, or through other avenues like itemDB or our discord. Let's say you look for the item Constellation Wings. As of September 2025, these are the trade reports:
        </Typography>
        <Box sx={{ textAlign: "center" }}>
          <img src={require('../../assets/constellation_wings_example.png')} alt="trade reports for constellation wings" style={{ maxWidth: "80%", borderRadius: 8, marginTop: 16 }} />
        </Box>
        <Typography paragraph>
          From past trade data, it seems to be trading at 1-2 caps, leaning a bit closer to 1. Therefore, it wouldn't be unreasonable to value it at 1-2 caps or even just 1 cap.
        </Typography>

        <Typography paragraph>
          As you can see, our value guide will also give a calculated value based on past trade reports. If you agree, you may certainly use that value, however, we always recommend reviewing trade data and making your own decision as some trade values can be all over the place. Your personal value may not necessarily agree with the past trade reports and calculated value and that's okay too!
        </Typography>

        <Typography paragraph>
          <b>What do you do when there is no trade history to go off of?</b> Make up your own value! Think about what value you would be most comfortable trading that item at. You can also look at the other methods like ratios and dates to get a feel for what you'd like. (And if you trade an item that has no trade data, please report it to us! We love data and it would help other users too!)
        </Typography>

        <Subheader title="Ratios" id="ratios" />

        <Typography paragraph>
          If you keep hearing about ratios and have no idea how to use them, this section is for you. Ratios in this context refer to the offering / seeking ratio on DTI or JN. Most of the time, DTI ratios are used for wearable items while JN is used for nonwearable items. Many traders use these ratios as a way to gauge supply (offering) and demand (seeking).
        </Typography>

        <Typography paragraph>
          For example, an item could have a calculated value of 3-4 due to past trade reports, but on DTI, there could be 200+ people offering and only 10 seeking. In this scenario, many traders would disagree with the value of 3-4, and likely value it closer to 1 if not a "junky" item.
        </Typography>

        <Typography paragraph>
          Likewise, an item could be listed as 2-3 but have 20 offering and 100 people seeking. It would be fair to value that item slightly higher, maybe to 4 caps or even more - whatever you're comfortable with!
        </Typography>

        <Typography paragraph>
          Shameless plug again but if you disagree with calculated value and make a trade, please report your trade with your personal values! The calculated values we have here are based off trade data and can remain outdated if there are no recent trades. The more reports we get, the more accurate we can be!
        </Typography>

        <Typography paragraph>
          Please also keep in mind that just like trade history, ratios do not necessarily tell the whole story. For example, let's say you come across an item with 150 offering and 60 seeking. You might think that's a 1 cap value since there's a lot more offering than seeking, but in reality, it could be trading at 3 caps since it was a previous LE item. You can still value the item lower as a personal value, though we believe it's also important to understand why/how something might have an existing established value from the community.
        </Typography>

        <Typography paragraph>
          There are so many different items and so many values and ranges on this site that there is no centrally agreed scale to say a 2-3 cap value has this specific offering to seeking ratio, 5 caps look like this, etc. You can develop your own scale if you'd like to use that to determine your personal value, but the only common cases that most traders would agree with are the extreme cases like earlier about 200+ offering / 10 seeking indicating a junky item.
        </Typography>

        <Typography paragraph>
          Please also remember that it takes time for people to notice items and add them to their wish/trade lists! Ratios can be an indicator of value, but it is not recommended to only judge by ratios when an item has just been released. The first hour of an item's release (even the first day!) is not usually an accurate representation of true demand/supply as many players have not even had the chance to log on. The ratios of offering/seeking can drastically change in the beginning so keep that in mind as you determine values!
        </Typography>

        <Subheader title="Retirement Date / Similar Items" id="retire-date" />
        <Typography paragraph>
          This method is more common for nonwearable items such as retired mystery capsules and event tickets. When there is no other trade data to go off of, some traders like to look at similar items (released/retired around same time, similar item type from that year, etc) that do have trade history and use that as their starting point.
        </Typography>

        <Typography paragraph>
          Other traders might like to value their items by adding a cap for every year that's passed since an item has been retired. For especially old items, some might add 2 caps per year once it hits a certain time since an item has retired.
        </Typography>

        <Typography paragraph>
          We do not have a specific formula beyond this because going by retirement date does not happen often (nonwearable trades don't happen often in general!), but we do want to acknowledge that this can be a way to gauge value as some collectors/traders use it. Nonwearables are a bit interesting because some of them are extremely hard to find, especially if an item is very old, but at the same time, mainly only collectors seek them so they can also be hard to trade!
        </Typography>

        <Typography paragraph>
          Note: If an item is rereleased (like the retired GBC capsule that came out earlier this year), that can greatly affect value too. It is not uncommon to disagree on values, especially with nonwearables!
        </Typography>
      </Section>

      <Section id="our-recommendations" title="Our Recommendations">
        <Typography paragraph
          sx={{ color: theme.palette.primary.main }}
        >
          Bottom line: <b>What value makes you the most comfortable trading for/away an item?</b>
        </Typography>

        <Typography paragraph>
          If you're unsure, ask yourself that as you gather information about the item. Start by checking past trade history. If there's a common value in recent trades, that is likely what the item is trading for in the community. In many cases, especially for new traders, that will suffice for getting a realistic value.
        </Typography>

        <Typography paragraph>
          If you have the time however, we recommend that you look at all the information available and find your own balance! Use trade history as a starting point, and look up the item's ratios - are a lot of people seeking it, offering it, or fairly equal? Check JN/ItemDB for the item's release history for even further insights and adjust the value as necessary.
        </Typography>

        <Typography paragraph>
          If you are new, we recommend that you also ask for value checks! The NC community on the boards and discord are filled with some wonderful people that would be happy to weigh in if you are unsure how to value something.
        </Typography>
      </Section>

      <Section id="common-scenarios" title="Common Scenarios">
        <Typography paragraph>
          Let's run how to approach some common scenarios.
        </Typography>

        <Typography
          sx={{ color: theme.palette.secondary.main }}
        >
          This just dropped in the mall - how do I trade it?
        </Typography>

        <Typography paragraph>
          New items need time to establish a value. The safest option is to wait and observe the boards to see what other people are trading it for.
        </Typography>

        <Typography paragraph>
          If you want to trade first and be a trailblazer, consider what type of item it is - a regular item? LE/bonus? If it's a normal item, it will likely start at 1 cap. If it's a dyeworks item, they typically can swap for other colors evenly or go for 1 cap or 1 dyepot.
        </Typography>

        <Typography paragraph>
          Bonuses/LEs depend on where they come from. As we covered previously, most regular cap LEs start at 3 caps. Pet day caps tend to start a bit higher depending on item quality. If it's a bonus from an event, consider how it costs to get the bonus. For example, this year's Negg Festival event had negg kits for 750NC. Each negg type had its own bonus, but you had to redeem all the other items first to get it. It would be fair to start the value at 3-4 caps, the equivalent to 750NC since someone could still buy a negg kit for 750NC and get their own bonus item.
        </Typography>

        <Typography paragraph>
          Additionally, what do you personally think of the item? Is it meh? Maybe it's just okay? Or maybe it's amazing and gorgeous? You can adjust the value based on that too!
        </Typography>

        <Typography
          sx={{ color: theme.palette.secondary.main }}
        >
          This item has no trade reports!
        </Typography>

        <Typography paragraph>
          Try the other methods that we talked about like checking ratios and release information. Does this item seem common? Are there many people who seem to want this item? Was this item an old bonus? Are there similar items like yours that do have a value? Use all of that information to construct a value!
        </Typography>

        <Typography paragraph>
          Extra tip: If there's people who have this item for trade on DTI, click the offering tab. You can see who has it up for trade, and what tradelist it's under. Sometimes, people name their list with their own personal value. So if you see your item under a few people's 2 GBC list, you can use that as a reference for your personal value!
        </Typography>

        <Typography
          sx={{ color: theme.palette.secondary.main }}
        >
          Someone offered a different value that doesn't seem fair to me!

        </Typography>

        <Typography paragraph>
          Disagreeing on values is okay and not uncommon at all! If you are both open to negotiating, you can work something out and meet in the middle. If you simply do not feel good about the value difference at all, feel free to decline! You can say something like “Thanks for the offer, but I value this item at X, so I'll pass!” and move on. The best trades are when both parties are happy with what they send and receive.
        </Typography>

        <Typography
          sx={{ color: theme.palette.secondary.main }}
        >
          I'm not sure if this person values this item the same as me.
        </Typography>

        <Typography paragraph>
          If you are ever unclear with values, there's no harm in asking! Especially with new items or items that don't seem to have much data, go ahead and say something like "Hi! What do you value your (item name) at?" If you know your personal value, you can mention it too and ask if they agree.
        </Typography>
      </Section>

      <Section id="lingering-questions">
        <AccordionFAQ subheader={
          <Typography
            variant="h4"
            gutterBottom
            sx={theme => ({
              color: theme.palette.accent.main
            })}>
            Lingering Questions
          </Typography>}
          items={faqItems} />
      </Section>

      <Section id="final-words" title="Final Words">
        <Typography paragraph>
          If you were forced to explain NC trading and NC economy to someone who doesn't play Neopets, you'd probably sound absolutely crazy. I felt absolutely insane just trying to write this article too. Thank you for sticking through this long read, and hopefully this has provided more insight and clarity into some of the weird nuances of NC trading! If not, please feel free to ask questions in the community! Happy Trading!
        </Typography>
      </Section>

    </GuideLayout>
  );
};

export default ValuesPage;
